import React, { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { ArchivedIcon, DeclinedInterviewIcon, HiredIcon, RejectIcon, ScheduleIcon } from '@modernloop/shared/icons';
import { Alert, AlertColor, AlertTitle, Theme } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';

import {
  ApplicationAlert_ApplicationFragment,
  ApplicationStatus,
  TaskFlagType,
  TaskStatus,
  UpcomingScheduleAlert_ApplicationStagesFragment,
} from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import { isEventInProgress, isUpcomingEvent } from 'src/views-new/CandidateDetails/utils';

const APPLICATION_ALERT_LABEL_KEYS: Record<ApplicationStatus, string> = {
  [ApplicationStatus.Active]: '',
  [ApplicationStatus.Archived]: 'hiring-dashboard-candidates:candidates.applicationAlert.statusArchived',
  [ApplicationStatus.Converted]: 'hiring-dashboard-candidates:candidates.applicationAlert.statusConverted',
  [ApplicationStatus.Hired]: 'hiring-dashboard-candidates:candidates.applicationAlert.statusHired',
  [ApplicationStatus.Rejected]: 'hiring-dashboard-candidates:candidates.applicationAlert.statusRejected',
};

type UpcomingScheduleAlertProps = {
  jobStageId: string;
};

type UpcomingScheduleAlertFragmentProps = {
  applicationStages: UpcomingScheduleAlert_ApplicationStagesFragment[];
};

type NewBaseApplicationAlertProps = {
  status: ApplicationStatus;
};

export const NewBaseApplicationAlert = ({ status }: NewBaseApplicationAlertProps) => {
  const { t } = useTranslation();
  const content = useMemo(() => {
    const labelKey = APPLICATION_ALERT_LABEL_KEYS[status];
    const label = labelKey ? t(labelKey) : '';
    let icon = <></>;
    let severity = 'info';

    switch (status) {
      case ApplicationStatus.Archived:
        icon = <ArchivedIcon />;
        severity = 'warning';
        break;

      case ApplicationStatus.Hired:
        icon = <HiredIcon />;
        severity = 'success';
        break;

      case ApplicationStatus.Converted:
        icon = <RejectIcon />;
        severity = 'warning';
        break;
      case ApplicationStatus.Rejected:
        icon = <RejectIcon />;
        severity = 'error';
        break;

      default:
    }

    return { label, icon, severity };
  }, [status, t]);

  return (
    <>
      {status !== ApplicationStatus.Active ? (
        <Alert icon={content.icon} severity={content.severity as AlertColor}>
          <AlertTitle>{content.label}</AlertTitle>
        </Alert>
      ) : null}
    </>
  );
};

// eslint-disable-next-line modernloop/restric-fragments-name.cjs
const UpcomingScheduleAlert: FCWithFragments<UpcomingScheduleAlertFragmentProps, UpcomingScheduleAlertProps> = ({
  applicationStages,
  jobStageId,
}) => {
  const { t } = useTranslation();
  const schedules = useMemo(
    () =>
      applicationStages.filter(
        (stage) => stage.jobStageId === jobStageId && stage.task?.status !== TaskStatus.Canceled
      ),
    [applicationStages, jobStageId]
  );

  if (!jobStageId) {
    return null;
  }

  const onGoingSchedule = schedules.find((schedule) => isEventInProgress(schedule.startAt, schedule.endAt));

  if (onGoingSchedule) {
    const hasInterviewerDeclinedFlag = onGoingSchedule.task?.flags?.find(
      (flag) => flag.flagType === TaskFlagType.InterviewDeclined && !flag.resolvedAt
    );
    const onGoingInterview = onGoingSchedule.applicationStageInterviews?.find((interview) =>
      isEventInProgress(interview.startAt, interview.endAt)
    );

    if (onGoingInterview) {
      const hasInterviewerDeclined = onGoingInterview.applicationStageInterviewInterviewers?.find(
        (interviewer) => interviewer.declines?.length
      );

      if (hasInterviewerDeclined && hasInterviewerDeclinedFlag) {
        return (
          <Alert
            sx={{
              marginBottom: (theme: Theme) => theme.spacing(1),
            }}
            severity="warning"
          >
            <AlertTitle>
              {t('hiring-dashboard-candidates:candidates.applicationAlert.interviewerDeclinedInProgress')}
            </AlertTitle>
          </Alert>
        );
      }
      return (
        <Alert severity="warning">
          <AlertTitle>{t('hiring-dashboard-candidates:candidates.applicationAlert.interviewsInProgress')}</AlertTitle>
        </Alert>
      );
    }

    const upcomingInterview = onGoingSchedule.applicationStageInterviews?.find((interview) =>
      isUpcomingEvent(interview?.startAt)
    );

    if (upcomingInterview) {
      const hasInterviewerDeclined = upcomingInterview.applicationStageInterviewInterviewers?.find(
        (interviewer) => interviewer.declines?.length
      );
      if (hasInterviewerDeclined && hasInterviewerDeclinedFlag) {
        return (
          <Alert
            sx={{
              marginBottom: (theme: Theme) => theme.spacing(1),
            }}
            icon={<DeclinedInterviewIcon color="error" />}
            severity="error"
          >
            <AlertTitle>
              {t('hiring-dashboard-candidates:candidates.applicationAlert.interviewerDeclinedBeginsIn', {
                time: formatDistanceToNow(new Date(upcomingInterview.startAt)),
              })}
            </AlertTitle>
          </Alert>
        );
      }
      return (
        <Alert
          sx={{
            marginBottom: (theme: Theme) => theme.spacing(1),
            alignItems: 'center',
          }}
          icon={<ScheduleIcon color="primary" />}
          severity="info"
        >
          <AlertTitle>
            {t('hiring-dashboard-candidates:candidates.applicationAlert.stageScheduledInterviewsBeginIn', {
              time: formatDistanceToNow(new Date(upcomingInterview.startAt)),
            })}
          </AlertTitle>
        </Alert>
      );
    }
  }

  const upcomingSchedule = schedules.find((schedule) => isUpcomingEvent(schedule?.startAt));

  if (upcomingSchedule) {
    const hasInterviewerDeclinedFlag = upcomingSchedule.task?.flags?.find(
      (flag) => flag.flagType === TaskFlagType.InterviewDeclined && !flag.resolvedAt
    );
    if (upcomingSchedule.hasInterviewerDeclined && hasInterviewerDeclinedFlag) {
      return (
        <Alert
          sx={{
            marginBottom: (theme: Theme) => theme.spacing(1),
          }}
          icon={<DeclinedInterviewIcon color="error" />}
          severity="error"
        >
          <AlertTitle>
            {t('hiring-dashboard-candidates:candidates.applicationAlert.interviewerDeclinedBeginsIn', {
              time: formatDistanceToNow(new Date(upcomingSchedule.startAt)),
            })}
          </AlertTitle>
        </Alert>
      );
    }

    return (
      <Alert
        sx={{
          marginBottom: (theme: Theme) => theme.spacing(1),
          alignItems: 'center',
        }}
        icon={<ScheduleIcon color="primary" />}
        severity="info"
      >
        <AlertTitle>
          {t('hiring-dashboard-candidates:candidates.applicationAlert.stageScheduledInterviewsBeginIn', {
            time: formatDistanceToNow(new Date(upcomingSchedule.startAt)),
          })}
        </AlertTitle>
      </Alert>
    );
  }

  return null;
};

UpcomingScheduleAlert.fragments = {
  applicationStages: gql`
    fragment UpcomingScheduleAlert_applicationStages on ApplicationStage {
      id
      jobStageId
      startAt
      endAt
      hasInterviewerDeclined
      task {
        id
        status
        flags {
          id
          flagType
          resolvedAt
        }
      }
      applicationStageInterviews {
        id
        startAt
        endAt
        applicationStageInterviewInterviewers {
          interviewerId
          declines {
            applicationStageInterviewId
          }
        }
      }
    }
  `,
};

type Props = {
  jobStageId: string;
};

type Fragments = {
  application: ApplicationAlert_ApplicationFragment;
};

const ApplicationAlert: FCWithFragments<Fragments, Props> = ({ application, jobStageId }) => {
  if (application.status === ApplicationStatus.Active && application.applicationStages) {
    return <UpcomingScheduleAlert applicationStages={application.applicationStages} jobStageId={jobStageId} />;
  }
  return <NewBaseApplicationAlert status={application.status} />;
};

ApplicationAlert.fragments = {
  application: gql`
    ${UpcomingScheduleAlert.fragments.applicationStages}
    fragment ApplicationAlert_application on Application {
      id
      status
      applicationStages(input: { isCancelled: false }) {
        ...UpcomingScheduleAlert_applicationStages
      }
    }
  `,
};

export default ApplicationAlert;
